import { ButtonProps } from "@/types/component"
import { ArrowDown as ArrowDownIcon } from "@/components/icons"

import "./index.scss"

import React, { useState, useRef, useEffect } from "react"

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  color = "default",
  disabled = false,
  endIcon,
  onClick = () => {},
  size = "medium",
  startIcon,
  type = "button",
  variant = "contained",
  dropdownItems,
  dropdownPosition = "bottom",
  leftAligned = false,
  loading = false,
  ...props
}) => {
  const [hover, setHover] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    
    if (dropdownItems && dropdownItems.length > 0) {
      e.preventDefault()
      setIsDropdownOpen(!isDropdownOpen)
    } else {
      onClick(e)
    }
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false)
    }
  }

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isDropdownOpen])

  const hasDropdown = dropdownItems && dropdownItems.length > 0
  const isDisabled = disabled || loading

  const buttonElement = (
    <button
      className={`button ${variant} ${color} ${size} ${isDisabled ? "disabled" : ""} ${hover ? "hover" : ""} ${hasDropdown ? "with-dropdown" : ""} ${isDropdownOpen ? "dropdown-open" : ""} ${leftAligned ? "left-aligned" : ""} ${loading ? "loading" : ""} ${className}`}
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      type={type}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <span className="button__spinner" aria-hidden />
      ) : (
        <>
          {startIcon && <span className="button__icon button__icon--start">{startIcon}</span>}
          <span className="button__text">{children}</span>
          {hasDropdown ? (
            <span className={`button__icon button__icon--dropdown ${isDropdownOpen ? "open" : ""}`}>
              <ArrowDownIcon height={24} width={24} />
            </span>
          ) : (
            endIcon && <span className="button__icon button__icon--end">{endIcon}</span>
          )}
        </>
      )}
    </button>
  )

  if (!hasDropdown) {
    return buttonElement
  }

  return (
    <div className="button-wrapper has-dropdown" ref={buttonRef}>
      {buttonElement}
      
      {isDropdownOpen && (
        <ul className={`button-dropdown ${dropdownPosition}`}>
          {dropdownItems.map((item, index) => (
            <li
              key={index}
              className="button-dropdown__item"
              onClick={() => {
                item.onClick()
                setIsDropdownOpen(false)
              }}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default Button
